import clsx, { ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatCreatorName } from "./formatters";
import { Nullable } from "./nullable";
import { CURRENCY_OPTIONS } from "./constants";
import { countries } from "./countries";
import { customAlphabet } from "nanoid";
import { addDays } from "date-fns";
import { z } from "zod/v4";

export { toast } from "sonner";

// Minimal structural type for creator name fields (no Drizzle dependency)
type CreatorNameFields = {
  knownAs: string | null;
  firstName: string | null;
  lastName: string | null;
};

export type TimePeriod = "morning" | "afternoon" | "evening";

/**
 * Returns the time period of day based on the browser's local hour.
 * Morning: 5–11, Afternoon: 12–17, Evening: 18–4.
 */
export const getTimePeriod = (): TimePeriod => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return "morning";
  }

  if (hour >= 12 && hour < 18) {
    return "afternoon";
  }

  return "evening";
};

/**
 * Combines multiple class names into a single string, merging Tailwind CSS classes.
 *
 * @example
 * import { cn } from "@drpcrd/utils";
 * const className = cn("bg-red-500", "text-white");
 */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/**
 * Sorts an array of creators by their formatted names.
 *
 * @remarks
 * This function is not optimized for performance. Consider re-writing or memoizing the result for better performance in the future.
 */
export const sortCreatorsByName = <
  C extends CreatorNameFields,
>(
  creators: C[],
): (C & { formattedName: string })[] =>
  creators
    .map((c) => ({ ...c, formattedName: formatCreatorName(c) }))
    .sort((a, b) => a.formattedName.localeCompare(b.formattedName));

/**
 * Removes duplicate objects from an array based on a specified key.
 */
export const removeDuplicatesById = <T extends Record<keyof T, unknown>>(
  creators: T[],
  idKey: keyof T,
): T[] => {
  const grouped = creators.reduce(
    (accumulator: Record<string | number, T>, creator) => {
      const id = creator[idKey] as string | number;
      if (!accumulator[id]) {
        accumulator[id] = creator;
      }
      return accumulator;
    },
    {} as Record<string | number, T>,
  );

  return Object.values(grouped);
};

/**
 * This function takes an array of creators from the database that have multiple socials and reduces them to a single creator with multiple socials.
 *
 * @example
 * input: [{"id": 1, "social": {"service": "tiktok"}}, {"id": 1, "social": {"service": "instagram"}}]
 * output: [{"id": 1, "socials": [{"service": "tiktok"}, {"service": "instagram"}]}]
 */
export const reduceCreatorsWithSocials = <
  T extends { id: string | number; social: Record<string, unknown> | null },
>(
  creators: T[],
): (Omit<T, "social"> & {
  socials: NonNullable<T["social"]>[];
})[] => {
  const grouped = creators.reduce(
    (accumulator, creator) => {
      const id = creator.id;
      if (!accumulator[id]) {
        const { social, ...rest } = creator;
        const creatorWithSocials = {
          ...rest,
          socials: social ? [social] : [],
        };
        accumulator[id] = creatorWithSocials;
      } else {
        if (creator.social) {
          accumulator[id].socials.push(
            creator.social as NonNullable<T["social"]>,
          );
        }
      }
      return accumulator;
    },
    {} as Record<
      string | number,
      Omit<T, "social"> & {
        socials: NonNullable<T["social"]>[];
      }
    >,
  );

  return Object.values(grouped);
};

/**
 * Safely parses a string into an integer.
 *
 * @param value - The string to be parsed. Can be null or undefined.
 * @returns The parsed integer, or null if the parsing fails.
 */
export const safeParseInt = (value: Nullable<string>) => {
  const parsed = value ? parseInt(value, 10) : NaN;
  return isNaN(parsed) ? null : parsed;
};

/**
 * Takes a float value and formats it to a number with X decimal places.
 * @example round(0.1234, 2) => 0.12
 */
export const round = (num: number, digits: number = 2) =>
  Math.round(num * Math.pow(10, digits)) / Math.pow(10, digits);

export const GENERATE_UUID_LENGTH = 12;
/**
 * Generates a UUID (Universally Unique Identifier) using a custom alphabet.
 *
 * @returns {string} A randomly generated UUID string.
 */
export const generateUUID = (): string => {
  const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
  return customAlphabet(alphabet, GENERATE_UUID_LENGTH)();
};

/**
 * Generates a default UTC date string, optionally defaulting to 6 PM and adding days.
 *
 * Used to generate Key Dates for the Deliverable Forms.
 */
export const generateDefaultUtcDate = (
  defaultTo6pm: boolean,
  addDaysBy: number,
): Date => {
  const utcDate = new Date(
    Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
      defaultTo6pm ? 18 : 0,
      0,
      0,
    ),
  );

  const finalDate = addDaysBy !== 0 ? addDays(utcDate, addDaysBy) : utcDate;

  return finalDate;
};

/**
 * Gets the currency symbol for a given currency code.
 *
 * @param currency - The currency code.
 * @returns The currency symbol.
 */
export const getCurrencySymbol = (currency: string): string =>
  CURRENCY_OPTIONS.find(
    (option) => option.value === currency.toUpperCase(),
  )?.label.charAt(0) || "";

/**
 * Finds a country by its value.
 *
 * @param value - The value of the country to find.
 * @returns The country object if found, otherwise undefined.
 */
export const findCountryByValue = (value: string) =>
  countries.find((country) => country.value === value);

/**
 * Escapes special characters in a string to make it safe for use in CSS selectors.
 * @param {string} value - The string to escape.
 * @returns {string} - The escaped string.
 */
export const cssEscape = (value: string): string =>
  value.replace(/([^\w-])/g, "\\$1");

/**
 * Truncates a string to the specified maximum length and adds an ellipsis if truncated.
 *
 * @param str - The string to truncate. Can be null or undefined.
 * @param maxLength - The maximum length of the resulting string (including ellipsis if added)
 * @returns The truncated string with ellipsis if needed, or the original string if shorter than maxLength
 */
export const truncate = (
  str: string | null | undefined,
  maxLength: number,
): string => {
  if (!str) return "";

  if (str.length <= maxLength) {
    return str;
  }

  return str.substring(0, maxLength - 3) + "...";
};

/**
 * Formats numeric values into abbreviated strings for display purposes.
 * For values 1,000 or more, displays one decimal place (e.g. 1.2K)
 * For values 10,000 or more, displays without decimals (e.g. 15K, not 16K)
 */
export const formatCompactNumber = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0";
  }

  if (value < 1000) {
    return value.toString();
  } else if (value < 10000) {
    return `${(value / 1000).toFixed(1)}K`;
  } else if (value < 1000000) {
    return `${Math.floor(value / 1000)}K`;
  } else if (value < 1000000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
};

/**
 * Formats seconds into hours:minutes:seconds format for TikTok metrics page
 */
export const formatTimeFromSeconds = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) {
    return "N/A";
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }
};

/**
 * Filters out null values, empty arrays, and empty objects from an object.
 *
 * @example
 * const data = { a: null, b: [], c: {}, d: "valid" };
 * const filtered = filterValidData(data);
 * // Result: { d: "valid" }
 */
export const filterValidData = (map: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(map).filter(([, value]) => {
      if (value == null) {
        return false;
      } else if (Array.isArray(value)) {
        return value.length > 0;
      } else if (typeof value === "object") {
        return Object.keys(value).length > 0;
      } else {
        return true;
      }
    }),
  );

/**
 * Generates a WhatsApp link for a given phone number.
 *
 * Example: "+1234567890" will return "https://wa.me/1234567890"
 */
export const getWhatsAppLink = (phoneNumber: string) => {
  const formattedNumber = phoneNumber.replace(/\D/g, "");
  return `https://wa.me/${formattedNumber}`;
};

/**
 * Safely retrieves a template from a template object by key
 */
export const getTemplate = <T extends Record<string, unknown>>(
  templates: T,
  templateKey: string,
): T[keyof T] | null => {
  type TemplateKey = keyof typeof templates;
  const template = templates[templateKey as TemplateKey];
  return template || null;
};

/**
 * Validates if a string is a valid email address format using Zod.
 *
 * @param email - The email string to validate
 * @returns True if the email format is valid, false otherwise
 *
 * @example
 * isValidEmail("user@example.com") // true
 * isValidEmail("invalid.email") // false
 */
export const isValidEmail = (email: string): boolean =>
  z.email().safeParse(email).success;

/**
 * Validates if a string is a valid URL using the URL constructor.
 * Returns false for URLs without a domain extension (except file:// protocol).
 *
 * @param urlString
 * @returns
 */
export const isValidUrl = (urlString?: string): boolean => {
  try {
    const url = new URL(urlString || "");
    if (!url.toString().includes(".") && url.protocol !== "file:") {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Calculates the median value from an array of numbers.
 * For even-length arrays, returns the ceiling of the average of the two middle values.
 * For odd-length arrays, returns the middle value.
 * Returns 0 for empty arrays.
 */
export const calculateMedian = (values: number[]): number => {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((a, b) => a - b);

  if (sorted.length % 2 === 0) {
    const mid1 = sorted[sorted.length / 2 - 1];
    const mid2 = sorted[sorted.length / 2];
    return Math.ceil((mid1 + mid2) / 2);
  }

  return sorted[Math.floor(sorted.length / 2)];
};

/**
 * Splits an array into chunks of a specified size.
 */
export const chunkArray = <T>(array: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
    array.slice(i * size, i * size + size),
  );
